import { Component, computed, OnDestroy, OnInit } from "@angular/core";
import { InspectionJob, Lot } from "../../../../core/database/db";
import { firstValueFrom, Subscription } from "rxjs";
import { TabsModule } from "primeng/tabs";
import { Router } from "@angular/router";
import { SkeletonModule } from "primeng/skeleton";
import { NetworkStatusService } from "../../../../shared/services/network-status.service";
import { JobService } from "../../../../domains/jobs/services/job.service";
import { CONSTANTS } from "../../../../constants";
import { OnlineIndicatorComponent } from "../../../../shared/components/online-indicator/online-indicator.component";
import { BackgroundRefreshIndicatorComponent } from "../../../../shared/components/background-refresh-indicator/background-refresh-indicator.component";
import { CustomerCareManagerScheduleService } from "../../../../domains/jobs/services/customer-care-manager-schedule.service";

export type CompleteInspectionJob = {
  lot: Lot
} & InspectionJob;

type JobsByDate = {
  date: string
  inspectionJobs: CompleteInspectionJob[]
}

@Component({
  selector: "app-complete-jobs",
  imports: [TabsModule, SkeletonModule, OnlineIndicatorComponent, BackgroundRefreshIndicatorComponent],
  templateUrl: "./complete-jobs.component.html",
  styleUrl: "./complete-jobs.component.scss"
})
export class CompleteJobsComponent implements OnInit, OnDestroy {
  salesCommunityNames: string[] = [];
  dataLoaded: boolean = false;
  online: boolean = false;
  onlineSubscription: Subscription;
  jobsByCommunity: { [salesCommunityDate: string]: JobsByDate[] } = {};
  readonly isRefreshingInBackground = computed(() => this.customerCareManagerScheduleService.isFetchingScheduleSignal());

  constructor(
    private jobService: JobService,
    private router: Router,
    private networkStatus: NetworkStatusService,
    private customerCareManagerScheduleService: CustomerCareManagerScheduleService) {
  }

  async ngOnInit() {
    this.onlineSubscription = this.networkStatus.online$.subscribe(async online => {
      this.online = online;
      if (this.online) {
        await this.fetchJobData();
      } else {
        this.dataLoaded = true;
      }
    });
  }

  async fetchJobData() {
    this.dataLoaded = false;
    this.jobsByCommunity = {};
    this.salesCommunityNames = [];

    const inspectionJobs = await this.getCompletedJobs();
    if (inspectionJobs === undefined) {
      this.dataLoaded = true;
      return;
    }

    for (const inspectionJob of inspectionJobs) {
      const salesCommunityName = inspectionJob.lot.salesCommunityName;

      // If we don't have an entry yet for this community name, create it
      if (!this.jobsByCommunity[salesCommunityName]) {
        this.jobsByCommunity[salesCommunityName] = [];
        this.salesCommunityNames.push(salesCommunityName);
      }

      const submittedAtDate = new Date(inspectionJob.submittedAt!);
      // Create string for date, e.g. 'November 21'
      const date = submittedAtDate.toLocaleString("en-US", { month: "long", day: "numeric" });
      // Try to get index of JobsByDate with date string we got above
      const jobsByDateIndex = this.jobsByCommunity[salesCommunityName].findIndex(i =>
        i.date === date
      );
      // If it didn't exist, we create it with the given string name and the inspectionJob we got
      if (jobsByDateIndex === -1) {
        this.jobsByCommunity[salesCommunityName].push({
          date,
          inspectionJobs: [inspectionJob]
        });
      } else { // If it did exist, add the current inspectionJob to the list of inspectionJobs
        this.jobsByCommunity[salesCommunityName][jobsByDateIndex].inspectionJobs.push(inspectionJob);
      }
    }
    this.dataLoaded = true;
  }
  async getCompletedJobs(): Promise<CompleteInspectionJob[] | undefined> {
    const completedJobs: CompleteInspectionJob[] | undefined =
      await firstValueFrom(this.jobService.getCompletedBQ7Jobs()).
        catch(_ =>
          undefined
        );
    if (completedJobs === undefined) {
      this.dataLoaded = true;
      return undefined;
    }
    // Only return BQ7 jobs
    const bq7Jobs = completedJobs.filter(job => job.buildQualityStep === CONSTANTS.BUILD_QUALITY_STEPS.BQ7);
    return bq7Jobs;
  }

  async navigateToJob(jobid: number, event?: MouseEvent) {
    event?.preventDefault();
    // const jobExistsInIndexedDB = await db.inspectionJob.get(jobid);
    this.router.navigateByUrl(`/ccm/bq7/job/${jobid}/report`, {
      state: {
        online: true // Always use API for completed jobs
      }
    });
  }

  addTabArrows() {
    return screen.width >= 1024;
  }

  async ngOnDestroy() {
    if (this.onlineSubscription) {
      this.onlineSubscription.unsubscribe();
    }

  }

  onTabRowWheel(event: WheelEvent): void {
    const target = event.currentTarget as HTMLElement;
    const tabViewport = target?.querySelector(".p-tablist-viewport");
    if (tabViewport) {
      const hasHorizontalOverflow = tabViewport.scrollWidth > tabViewport.clientWidth;
      if (hasHorizontalOverflow) {
        event.preventDefault();
        tabViewport.scrollLeft += (event.deltaY * 1.2);
      }
    }
  }
}
